import { useEffect, useRef } from "react";

/**
 * useOverlayBehavior — shared Esc-to-close, body-scroll-lock, and
 * focus-on-open/restore-on-close behavior for portal-rendered overlays
 * (Modal, Drawer). One place to change the interaction contract for
 * every dismissible overlay in the app.
 */
export function useOverlayBehavior(
  open: boolean,
  onClose: () => void,
  { closeOnEsc = true }: { closeOnEsc?: boolean } = {}
) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const previousFocus = useRef<HTMLElement | null>(null);

  // Esc-to-close.
  useEffect(() => {
    if (!open || !closeOnEsc) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, closeOnEsc, onClose]);

  // Body scroll lock while open.
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  // Focus first interactive element on open; restore focus to the trigger on close.
  useEffect(() => {
    if (!open) return;
    previousFocus.current = document.activeElement as HTMLElement | null;
    const panelEl = panelRef.current;
    if (panelEl) {
      const focusable = panelEl.querySelector<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      focusable?.focus();
    }
    return () => {
      previousFocus.current?.focus?.();
    };
  }, [open]);

  return panelRef;
}
